import React from "react";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/CategoryPage.css";

export default function CategoryPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  
  // List of ready games (only alphabet games for now)
  const readyGames = ["/alphabet-puzzle", "/balloon-letter-pop", "/number-popup-game", "/animal-game"];
  
  // Sample data matching the dashboard
  const categories = [
    {
      id: "alphabet",
      title: "Alphabet",
      icon: "🔤",
      color: "#4CAF50",
      gradient: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
      games: [
        {
          id: "alphabet-puzzle",
          title: "Alphabet Puzzle",
          description: "Put the letters in order",
          icon: "🧩",
          path: "/alphabet-puzzle",
          difficulty: "Easy",
          duration: "5-10 min"
        },
        {
          id: "balloon-pop",
          title: "Balloon Alphabet Pop",
          description: "Pop balloons with letters",
          icon: "🎈",
          path: "/balloon-letter-pop",
          difficulty: "Medium",
          duration: "10-15 min"
        }
      ]
    },
    {
      id: "numbers",
      title: "Numbers",
      icon: "🔢",
      color: "#2196F3",
      gradient: "linear-gradient(135deg, #2196F3 0%, #0D47A1 100%)",
      games: [
        {
          id: "number-match",
          title: "Number Popup Game",
          description: "Match numbers with quantities",
          icon: "➗",
          path: "/number-popup-game",
          difficulty: "Easy",
          duration: "5-10 min"
        }
      ]
    },

 {
      id: "animals",
      title: "Animals",
      icon: "🐶",
      color: "#795548",
      gradient: "linear-gradient(135deg, #795548 0%, #4E342E 100%)",
      games: [
        {
          id: "animal-sounds",
          title: "Animal Sounds",
          description: "Match animals with their sounds",
          icon: "🐱",
          path: "/animal-game",
          difficulty: "Easy",
          duration: "5-10 min"
        }
      ]
    },

    {
      id: "shapes",
      title: "Shapes",
      icon: "⭐",
      color: "#FF9800",
      gradient: "linear-gradient(135deg, #FF9800 0%, #EF6C00 100%)",
      games: [
        {
          id: "shape-sorter",
          title: "Shape Sorter",
          description: "Drag shapes to matching holes",
          icon: "🔺",
          path: "/shape-sorter",
          difficulty: "Medium",
          duration: "10-15 min"
        }
      ]
    },
    {
      id: "colors",
      title: "Colors",
      icon: "🌈",
      color: "#E91E63",
      gradient: "linear-gradient(135deg, #E91E63 0%, #AD1457 100%)",
      games: [
        {
          id: "color-mixer",
          title: "Color Mixer",
          description: "Mix primary colors to make new ones",
          icon: "🎨",
          path: "/color-mixer",
          difficulty: "Medium",
          duration: "10-15 min"
        }
      ]
    },
   
    {
      id: "words",
      title: "First Words",
      icon: "📝",
      color: "#9C27B0",
      gradient: "linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)",
      games: [
        {
          id: "word-match",
          title: "Word Match",
          description: "Match words with pictures",
          icon: "🔤",
          path: "/word-match",
          difficulty: "Easy",
          duration: "5-10 min"
        }
      ]
    }
  ];

  const currentCategory = categories.find(cat => cat.id === categoryId);

  const handleGameSelect = (path) => {
    if (readyGames.includes(path)) {
      navigate(path);
    }
    // Do nothing if the game is not ready
  };

  const goBack = () => {
    navigate(-1);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.3 
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 120 }
    }
  };

  if (!currentCategory) {
    return (
      <div className="category-page-container">
        <div className="container text-center py-5">
          <h2>Category not found</h2>
          <button onClick={goBack} className="back-btn">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="category-page-container">
      <div className="category-background"></div>
      
      <motion.div
        className="container-fluid py-4 px-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header with back button */}
        <motion.div 
          className="d-flex align-items-center mb-4"
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <button className="back-btn" onClick={goBack}>
            &larr; Back
          </button>
        </motion.div>

        <header className="text-center mb-4">
          <motion.div 
            className="category-header-icon mb-3"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.3 }}
            style={{ background: currentCategory.gradient }}
          >
            {currentCategory.icon}
          </motion.div>
          <motion.h1 
            className="display-4 fw-bold title-text"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 120 }}
          >
            {currentCategory.title} Games
          </motion.h1>
          <motion.p 
            className="lead subtitle"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 120 }}
          >
            Fun ways to learn about {currentCategory.title.toLowerCase()}!
          </motion.p>
        </header>

        {/* Games Grid - Now properly aligned 2 per row on mobile */}
        <motion.div 
          className="games-section"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="row g-2 g-sm-3 justify-content-center">
            {currentCategory.games.map(game => {
              const isReady = readyGames.includes(game.path);
              return (
                <div key={game.id} className="col-6 col-sm-6 col-md-4 col-lg-4 col-xl-3">
                  <motion.div 
                    className="game-card h-100"
                    onClick={() => handleGameSelect(game.path)}
                    cursor={isReady ? 'pointer' : 'not-allowed'}
                     disabled={!isReady}
                    variants={itemVariants}
                    whileHover={{ 
                      y: isReady ? -10 : 0,
                      scale: isReady ? 1.02 : 1,
                      transition: { type: "spring", stiffness: 300 }
                    }}
                    whileTap={{ scale: isReady ? 0.97 : 1 }}
                  >
                    <div className="game-icon-wrapper" style={{ background: currentCategory.gradient }} >
                      <div className="game-icon">{game.icon}</div>
                    </div>
                    <h3 className="h5 game-title">{game.title}</h3>
                    <p className="mb-3 game-description">{game.description}</p>
                    
                    <div className="game-meta d-flex justify-content-between mb-3">
                      <span className="game-difficulty">{game.difficulty}</span>
                      <span className="game-duration">{game.duration}</span>
                    </div>
                    
                    {!isReady && (
                      <div className="coming-soon-badge">Coming Soon</div>
                    )}
                    
                    <motion.button 
                      className={`play-btn w-100 ${isReady ? '' : 'disabled'}`}
                      whileHover={isReady ? { scale: 1.05 } : {}}
                      whileTap={isReady ? { scale: 0.95 } : {}}
                      onClick={() => handleGameSelect(game.path)}
                      style={{ 
                        background: isReady ? currentCategory.gradient : '#ccc',
                        cursor: isReady ? 'pointer' : 'not-allowed'
                      }}
                      disabled={!isReady}
                    >
                      {isReady ? 'Play Now' : 'Coming Soon'}
                    </motion.button>
                  </motion.div>
                </div>
              );
            })}
          </div>
        </motion.div>
        
        {/* Category description */}
        <motion.div 
          className="category-description mt-5 p-4 rounded-4"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          style={{ background: currentCategory.gradient }}
        >
          <h2 className="h3 mb-3">About {currentCategory.title} Learning</h2>
          <p className="mb-0">
            {currentCategory.title} games help children develop early literacy and cognitive skills 
            through interactive play. These games make learning fun and engaging while building 
            important foundational knowledge.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}